import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { marker as _ } from '@vandres/ngx-translate-extract-marker';

import { OdinService } from '../../../core/services/odin.service';
import { Room, RoomsStore } from '../../../state/rooms.repository';
import { Button } from '../../../shared/components/ui/buttons/gradient-btn/button';
import { isWithinSchedule } from '../../../shared/models/schedule';

/**
 * "Waiting for a moderator" overlay shown while a gated join (knock-to-join) is
 * pending approval. Reads `OdinService.pendingApproval`; renders nothing when
 * there is no pending request. The message is tailored from the target room:
 * a private room reads as a plain admit request, whereas a public room joined
 * outside its schedule window explains the meeting hasn't started yet.
 */
@Component({
  selector: 'andor-join-approval-overlay',
  standalone: true,
  imports: [MatProgressSpinner, TranslatePipe, Button],
  templateUrl: './join-approval-overlay.html',
  styleUrl: './join-approval-overlay.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JoinApprovalOverlay {
  readonly #odin = inject(OdinService);
  readonly #rooms = inject(RoomsStore);
  readonly #rooms$ = toSignal(this.#rooms.rooms$, { initialValue: [] as Room[] });

  protected readonly pending = this.#odin.pendingApproval;

  protected readonly reason = computed(() => {
    const p = this.pending();
    if (!p) return '';
    const room = this.#rooms$().find((r) => r.roomId === p.roomId);
    const outsideSchedule = !!room?.schedule && !isWithinSchedule(room.schedule, Date.now());
    if (room && !room.isPrivate && outsideSchedule) {
      return _("The meeting hasn't started yet — a moderator can let you in early.");
    }
    return _('Waiting for a moderator to admit you…');
  });

  protected cancel(): void {
    this.#odin.cancelPendingApproval();
  }
}
